export class AppOverlay extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        this.render();
        this.setupEvents();
    }

    show() {
        const version = document.getElementById('app-version')?.textContent || "Unknown";
        this.shadowRoot.querySelector('.meta').textContent = `Version ${version}`;
        this.shadowRoot.getElementById('overlay').classList.remove('hidden');
    }

    hide() {
        this.shadowRoot.getElementById('overlay').classList.add('hidden');
    }

    setupEvents() {
        const closeBtn = this.shadowRoot.getElementById('close-btn');
        const closeBtnX = this.shadowRoot.getElementById('close-btn-x');
        const overlay = this.shadowRoot.getElementById('overlay');
        const reportBtn = this.shadowRoot.getElementById('report-btn');

        const hideHandler = () => this.hide();

        // Prevent interaction with performance surface
        const stopProp = (e) => e.stopPropagation();
        overlay.addEventListener('mousedown', stopProp);
        overlay.addEventListener('touchstart', stopProp, { passive: true });
        overlay.addEventListener('click', stopProp);

        closeBtn.addEventListener('click', hideHandler);
        closeBtnX.addEventListener('click', hideHandler);
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) hideHandler();
        });
        
        reportBtn.addEventListener('click', () => {
             const version = document.getElementById('app-version')?.textContent || "Unknown";
             window.open(`mailto:bugs@sonicenv.app?subject=Bug Report: Sonic Envelope v${version}`, '_blank');
        });
    }

    render() {
        // Initial render with potential empty version, show() will update it
        const version = document.getElementById('app-version')?.textContent || "Unknown";
        
        // CSS
        const style = `
            <style>
                :host {
                    z-index: 2000;
                    position: relative;
                }
                #overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.6);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    opacity: 1;
                    transition: opacity 0.3s ease;
                    z-index: 2000;
                }
                #overlay.hidden {
                    opacity: 0;
                    pointer-events: none;
                }
                .content {
                    background: rgba(20, 20, 20, 0.9);
                    border: 1px solid rgba(0, 255, 204, 0.3);
                    border-radius: 16px;
                    padding: 40px;
                    width: 90%;
                    max-width: 500px;
                    text-align: center;
                    box-shadow: 0 0 50px rgba(0, 0, 0, 0.8), 0 0 15px rgba(0, 255, 204, 0.2);
                    position: relative;
                    transform: translateY(0);
                    transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                #overlay.hidden .content {
                    transform: translateY(20px);
                }
                h2 {
                    color: #00ffcc;
                    margin: 20px 0 10px;
                    font-family: 'Courier New', monospace;
                    text-transform: uppercase;
                    letter-spacing: 3px;
                }
                p {
                    color: #ccc;
                    font-family: 'Courier New', monospace;
                    line-height: 1.6;
                    font-size: 0.9rem;
                }
                .meta {
                    color: #666;
                    font-size: 0.8rem;
                    margin-bottom: 30px;
                }
                .btn-group {
                    display: flex;
                    gap: 15px;
                    justify-content: center;
                    margin-top: 25px;
                }
                button {
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    color: #fff;
                    padding: 10px 20px;
                    border-radius: 4px;
                    font-family: inherit;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-transform: uppercase;
                    font-size: 0.8rem;
                    letter-spacing: 1px;
                }
                button.primary {
                    border-color: #00ffcc;
                    color: #00ffcc;
                }
                button:hover {
                    background: rgba(255, 255, 255, 0.1);
                    transform: translateY(-2px);
                }
                button.primary:hover {
                    background: rgba(0, 255, 204, 0.1);
                    box-shadow: 0 0 15px rgba(0, 255, 204, 0.3);
                }
                #close-btn-x {
                    position: absolute;
                    top: 15px;
                    right: 15px;
                    background: none;
                    border: none;
                    color: #666;
                    font-size: 1.5rem;
                    padding: 0;
                    cursor: pointer;
                }
                #close-btn-x:hover {
                    color: #fff;
                }
                .feature-icons {
                    display: flex;
                    justify-content: center;
                    gap: 20px;
                    margin-top: 20px;
                    color: #888;
                }
                .feature-icon {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 5px;
                    font-size: 0.6rem;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }
                .feature-icon svg {
                    width: 24px;
                    height: 24px;
                }
            </style>
        `;

        // HTML
        const html = `
            <div id="overlay" class="hidden">
                <div class="content">
                    <button id="close-btn-x">×</button>
                    <app-logo class="large"></app-logo>
                    <h2>Sonic Envelope</h2>
                    <p class="meta">Version ${version}</p>
                    <p>
                        An experimental touch-first synthesizer designed for the modern web. 
                        Explore new sonic territories with gesture-controlled audio synthesis.
                    </p>
                    <div class="feature-icons">
                        <div class="feature-icon" title="WebMidi Supported">
                            <svg viewBox="0 0 24 24"><path fill="currentColor" d="M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-9 12.5v-1c0-.28.22-.5.5-.5s.5.22.5.5v1c0 .28-.22.5-.5.5s-.5-.22-.5-.5zm0 3v-1c0-.28-.22-.5.5-.5s.5.22.5.5v1c0 .28-.22.5-.5.5s-.5-.22-.5-.5zM15 15c.28 0 .5.22.5.5v2c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-2c0-.28.22-.5.5-.5zm-4-6.5v1c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-1c0-.28.22-.5.5-.5s.5.22-.5.5zm-3 2v1c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-1c0-.28.22-.5.5-.5s.5.22-.5.5zm3-2v1c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-1c0-.28.22-.5.5-.5s.5.22-.5.5zm3 2v1c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-1c0-.28.22-.5.5-.5s.5.22-.5.5zm-3 8.5v1c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-1c0-.28.22-.5.5-.5s.5.22-.5.5z"/></svg>
                            <span>MIDI</span>
                        </div>
                        <div class="feature-icon" title="QWERTY Keyboard Supported">
                            <svg viewBox="0 0 24 24"><path fill="currentColor" d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z"/></svg>
                            <span>Keys</span>
                        </div>
                        <div class="feature-icon" title="Multitouch Support">
                            <svg viewBox="0 0 24 24"><path fill="currentColor" d="M18 2c1.1 0 2 .9 2 2v16c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h12zm0 2H6v16h12V4zM9 18h6v-2H9v2zm3-14c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"/></svg>
                            <span>Touch</span>
                        </div>
                    </div>
                    <div class="btn-group">
                        <button id="report-btn" class="primary">Report Bug</button>
                        <button id="close-btn">Close</button>
                    </div>
                </div>
            </div>
        `;

        this.shadowRoot.innerHTML = style + html;
    }
}

customElements.define('app-overlay', AppOverlay);
